import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Save, Landmark, Trash2 } from "lucide-react";

export default function FactuurboekLeveranciers() {
  const { canWrite } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [newAcct, setNewAcct] = useState("");
  const [acctBusy, setAcctBusy] = useState(false);

  const loadAccounts = () => { api.get("/factuurboek/supplier-accounts").then((r) => setAccounts(r.data.accounts)).catch(() => setAccounts([])); };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/factuurboek/suppliers"), api.get("/factuurboek/categories")])
      .then(([s, c]) => { setSuppliers(s.data.suppliers); setCategories(c.data.categories); })
      .finally(() => setLoading(false));
    loadAccounts();
  };
  useEffect(() => { load(); }, []);

  const addAccount = async () => {
    const name = newAcct.trim();
    if (!name) return;
    setAcctBusy(true);
    try {
      const r = await api.post("/factuurboek/supplier-accounts", { name });
      toast.success(`Rekening ${r.data.code} aangemaakt voor ${name}`);
      setNewAcct(""); loadAccounts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAcctBusy(false); }
  };
  const deleteAccount = async (a) => {
    if (!window.confirm(`Leveranciersrekening ${a.code} (${a.name}) verwijderen uit de automatische herkenning? Bestaande boekingen behouden hun rekening.`)) return;
    try {
      await api.delete(`/factuurboek/supplier-accounts/${a.code}`);
      toast.success("Rekening verwijderd");
      loadAccounts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setField = (id, field, val) => {
    setSuppliers((list) => list.map((s) => (s.id === id ? { ...s, [field]: val } : s)));
    setDirty((d) => ({ ...d, [id]: true }));
  };

  const save = async (s) => {
    try {
      await api.patch(`/factuurboek/suppliers/${s.id}`, {
        name: s.name, vat_number: s.vat_number, address: s.address, default_category: s.default_category,
      });
      toast.success("Leverancier opgeslagen");
      setDirty((d) => ({ ...d, [s.id]: false }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="factuur-suppliers-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Users className="h-7 w-7 text-indigo-600" /> Leveranciers
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Standaard-kostencategorie per leverancier. Nieuwe facturen krijgen automatisch deze categorie.</p>
      </div>
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Naam</TableHead><TableHead>BTW-nummer</TableHead><TableHead>Standaardcategorie</TableHead>
                <TableHead className="text-right">Facturen</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <TableCell><Input className="h-8" value={s.name} disabled={!canWrite} onChange={(e) => setField(s.id, "name", e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 font-mono" value={s.vat_number} disabled={!canWrite} onChange={(e) => setField(s.id, "vat_number", e.target.value)} /></TableCell>
                    <TableCell>
                      <Select value={s.default_category || ""} onValueChange={(v) => setField(s.id, "default_category", v)} disabled={!canWrite}>
                        <SelectTrigger className="h-8" data-testid={`supplier-cat-${s.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{s.invoice_count}</TableCell>
                    <TableCell className="text-right">
                      {canWrite && <Button size="sm" variant={dirty[s.id] ? "default" : "outline"} className={dirty[s.id] ? "bg-indigo-600 hover:bg-indigo-700 h-8" : "h-8"} data-testid={`supplier-save-${s.id}`} onClick={() => save(s)}><Save className="h-3.5 w-3.5 mr-1.5" /> Opslaan</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {suppliers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nog geen leveranciers. Upload facturen in het Factuurboek.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm" data-testid="supplier-accounts-card">
        <CardContent className="p-4 space-y-3">
          <div>
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Landmark className="h-5 w-5 text-indigo-600" /> Leveranciersrekeningen (440-plan)</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Eigen 440xxx-subrekeningen voor kaartbetalingen (Viva/BNP). Maak hier bv. een <b>Aldi</b>-rekening aan zodat Aldi-kaartbetalingen op hun eigen rekening boeken i.p.v. 440000.</p>
          </div>
          {canWrite && (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-muted-foreground">Naam nieuwe leveranciersrekening</label>
                <Input className="h-9 mt-0.5" placeholder="bv. Aldi Sint-Truiden" value={newAcct} data-testid="new-supplier-account-name" onChange={(e) => setNewAcct(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAccount()} />
              </div>
              <Button className="h-9 bg-indigo-600 hover:bg-indigo-700" disabled={acctBusy || !newAcct.trim()} data-testid="add-supplier-account" onClick={addAccount}>+ Rekening toevoegen</Button>
            </div>
          )}
          <div className="rounded-lg border divide-y max-h-96 overflow-auto" data-testid="supplier-accounts-list">
            {accounts.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">Nog geen subrekeningen.</div>}
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`supplier-account-${a.code}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs mr-2">{a.code}</span>
                  <span className="truncate">{a.name || "—"}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-muted-foreground">{a.usage} boeking{a.usage === 1 ? "" : "en"}</span>
                  {canWrite && <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`delete-supplier-account-${a.code}`} onClick={() => deleteAccount(a)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
